import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import { BankSuggestions, PartySuggestions } from "react-dadata";
import "react-dadata/dist/react-dadata.css";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useRevalidator, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { Awesome, DevModal } from "@/components/Helpers";
import {
    DeveloperCountry,
    DeveloperEdoOperator,
    DeveloperFinanceModeration,
    DeveloperIdDocumentType,
    DeveloperLegalForm,
    DeveloperResidenceTerr,
    DeveloperState,
} from "@/const";
import { useRequest } from "@components/AppHook";
import { LicenseText } from "@components/Developer/Misc";
import {
    DevForm,
    FormFieldErrorMessage,
    FormFieldGroup,
    FormFieldHelpText,
    FormFieldLenCount,
} from "@components/Forms/Fields";
import { buildFormData, fieldChange, useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function PaymentInfo({ developerId, header }) {
    const { t } = useTranslation();
    const params = useParams();
    const { tab } = params;
    const { apiData } = useAppApi(url_reverse("api:developer:payment_info", { developer_id: developerId, tab }));
    const navigate = useNavigate();
    const commonInfo = useRouteLoaderData("commonInfo");
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;

    if (!apiData) return;

    const handleSelect = (k) => {
        navigate({
            pathname: url_reverse("devs:payment_info", { dev_id: developerId, tab: k }),
        });
    };

    const headerTools = <DeveloperFinanceModerationBadge state={developer.finance_moderation} />;
    return (
        <>
            <CommonCard header={header} headerTools={headerTools}>
                {developer.legal_form ? (
                    <Tabs
                        activeKey={tab}
                        transition={false}
                        className="react-bootstrap-tabs-fix"
                        onSelect={handleSelect}
                    >
                        <Tab
                            eventKey="legal_info"
                            title={
                                <>
                                    {t("Юридическая информация")}{" "}
                                    {apiData.legal_info_filled || <span className="asteriskField">*</span>}
                                </>
                            }
                        >
                            {tab === "legal_info" && <TabLegalInfo />}
                        </Tab>
                        {developer.country !== null && !isEnKontur(developer) && (
                            <Tab
                                eventKey="id_doc_data"
                                title={
                                    <>
                                        {t("Паспорт/регистрация")}{" "}
                                        {apiData.id_doc_data_filled || <span className="asteriskField">*</span>}
                                    </>
                                }
                            >
                                {tab === "id_doc_data" && <TabIdDocData />}
                            </Tab>
                        )}
                        {developer.country !== null && (
                            <Tab
                                eventKey="pay_info"
                                title={
                                    <>
                                        {t("Платежная информация")}{" "}
                                        {apiData.pay_info_filled || <span className="asteriskField">*</span>}
                                    </>
                                }
                            >
                                {tab === "pay_info" && <TabPayInfo />}
                            </Tab>
                        )}
                        {developer.country !== null && (
                            <Tab
                                eventKey="docs_flow"
                                title={
                                    <>
                                        {t("Документооборот")}{" "}
                                        {apiData.docs_flow_filled || <span className="asteriskField">*</span>}
                                    </>
                                }
                            >
                                {tab === "docs_flow" && <TabDocsFlow />}
                            </Tab>
                        )}
                        {developer.country !== null && commonInfo?.account.is_super && (
                            <Tab
                                eventKey="administration"
                                title={
                                    <>
                                        <Awesome type="flash" color="red" /> {t("Администрирование")}
                                    </>
                                }
                            >
                                {tab === "administration" && <TabAdministration />}
                            </Tab>
                        )}
                    </Tabs>
                ) : tab === "legal_info" ? (
                    <SelectLegalFormForm />
                ) : (
                    "Ooops"
                )}
            </CommonCard>
        </>
    );
}

function SelectLegalFormForm() {
    const { t } = useTranslation();
    const { setApiReget } = useAppContext();

    return <RenderForm submitButtonText={t("Далее")} afterRespOK={() => setApiReget(true)} colLabel={4} colField={8} />;
}

function TabLegalInfo() {
    const { t } = useTranslation();
    const [newLegalForm, setNewLegalForm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const onFormSubmit = useSubmit();
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;
    const doAction = usePaymentAction();

    const openModalWithLegalForm = (ev) => {
        setShowModal(true);
        setNewLegalForm(ev.val);
    };

    const modalClose = () => {
        setShowModal(false);
    };

    return (
        <>
            <Form onSubmit={onFormSubmit}>
                <DevForm.Field prefix="main" name="legal_form" onChange={openModalWithLegalForm} />
                <DevForm.Field prefix="main" name="residence_terr" />
                <DevForm.Field prefix="main" name="vkp_offer_accept" />
                <DevForm.Field
                    prefix="main"
                    name="country"
                    onChange={(ev) => {
                        doAction(developer.id, { action: "change_country", country: ev.val });
                    }}
                />
                <DevForm.Field prefix="main" name="uae_freezone" />
                {isEnKontur(developer) ? (
                    <DocumentationLink
                        url="https://documentation.vkplay.ru/terms_vkp/developer_agreement_grn"
                        info={<Awesome type="info-circle" color="grey" tooltip={<LicenseText />} />}
                    />
                ) : (
                    <DocumentationLink url="https://documentation.vkplay.ru/terms_vkp/developer_agreement_vkp" />
                )}
                <FieldTaxId />
                <DevForm.Field prefix="main" name="tax_id_file" />
                <DevForm.Field prefix="main" name="company_ru" />
                <DevForm.Field prefix="main" name="tax_residency_cert" />
                <DevForm.Field prefix="main" name="registration_number" />
                <DevForm.Field prefix="main" name="kpp" />
                <Buttons legal_info={true} />
                {developer.finance_moderation === DeveloperFinanceModeration.REJECTED &&
                    developer.moderator_comment && (
                        <div className="dmg-callout dmg-callout-warning mt-3">
                            <div>
                                <b>{t("Комментарий модератора")}</b>
                            </div>
                            {parse(developer.moderator_comment.replace(/\n/g, "<br>"))}
                        </div>
                    )}
            </Form>

            <DevModal
                show={showModal}
                onHide={modalClose}
                header={t("Смена правового статуса")}
                body={t(
                    "При смене правового статуса все заполненные значения в полях будут удалены. Вы уверены, что хотите переключить правовой статус?"
                )}
                footer={
                    <>
                        <DevForm.Button
                            variant="primary"
                            name="change_legal_form"
                            onClick={() =>
                                doAction(developer.id, { action: "change_legal_form", legal_form: newLegalForm })
                            }
                        >
                            {t("Сменить")}
                        </DevForm.Button>

                        <DevForm.Button variant="secondary" name="cancel" onClick={modalClose}>
                            {t("Отмена")}
                        </DevForm.Button>
                    </>
                }
            />
        </>
    );
}

function TabIdDocData() {
    const { apiData } = useAppContext();
    const onFormSubmit = useSubmit();

    const isRuPassport =
        String(apiData.forms_data.main.data.id_document_type) ===
        String(DeveloperIdDocumentType.PASSPORT_RUSSIAN_FEDERATION);

    return (
        <Form onSubmit={onFormSubmit}>
            <DevForm.Field prefix="main" name="surname" />
            <DevForm.Field prefix="main" name="forename" />
            <DevForm.Field prefix="main" name="patronymic" />
            <DevForm.Field prefix="main" name="birth_date" />
            <DevForm.Field prefix="main" name="citizenship" />
            <DevForm.Field prefix="main" name="id_document_type" />
            {isRuPassport ? <FieldIdDocumentSeriesNumber /> : <DevForm.Field prefix="main" name="id_document_number" />}
            <DevForm.Field prefix="main" name="id_document_date" />
            <DevForm.Field prefix="main" name="id_document_org" />
            {isRuPassport && <FieldIdDocumentDivCode />}
            {isRuPassport && <FieldSnils />}
            <DevForm.Field prefix="main" name="id_document_file" />
            <DevForm.Field prefix="main" name="region_rf" />
            <DevForm.Field prefix="main" name="region" />
            <DevForm.Field prefix="main" name="city" />
            <DevForm.Field prefix="main" name="street" />
            <DevForm.Field prefix="main" name="house" />
            <DevForm.Field prefix="main" name="building" />
            <DevForm.Field prefix="main" name="apartment" />
            <DevForm.Field prefix="main" name="postal_code" />
            <Buttons />
        </Form>
    );
}

function TabDocsFlow() {
    const { apiData, setApiData } = useAppContext();

    useEffect(() => {
        const data = apiData.forms_data?.main.data;
        if (data) {
            const newApiData = { ...apiData };
            if (!newApiData.forms_data.main.fields.edo_operator.disabled) {
                newApiData.forms_data.main.fields.edo_operator.required = true;
                newApiData.forms_data.main.fields.edo_operator.widget.is_hidden = !data?.edo_is_using;
            }
            if (!newApiData.forms_data.main.fields.edo_guid.disabled) {
                newApiData.forms_data.main.fields.edo_guid.required = true;
                newApiData.forms_data.main.fields.edo_guid.widget.is_hidden = !(
                    data?.edo_is_using && data.edo_operator === DeveloperEdoOperator.OTHER
                );
            }
            setApiData(newApiData);
        }
    }, [JSON.stringify(apiData)]);

    return <RenderForm buttons={<Buttons />} />;
}

function TabPayInfo() {
    const onFormSubmit = useSubmit();

    return (
        <Form onSubmit={onFormSubmit}>
            <DevForm.Field prefix="main" name="payment_receiver" />
            <DevForm.Field prefix="main" name="currency" />
            <FieldBicCode />
            <DevForm.Field prefix="main" name="beneficiary_bank_name" />
            <DevForm.Field prefix="main" name="beneficiary_bank_address" />
            <DevForm.Field prefix="main" name="iban" />
            <DevForm.Field prefix="main" name="corr_acc_bank" />
            <DevForm.Field prefix="main" name="corr_bic_code" />
            <DevForm.Field prefix="main" name="beneficiary_bank" />
            <DevForm.Field prefix="main" name="corr_acc_corr_bank" />
            <DevForm.Field prefix="main" name="corr_iban" />
            <DevForm.Field prefix="main" name="addr_recipient_income" />
            <DevForm.Field prefix="main" name="swift_code" />
            <DevForm.Field prefix="main" name="swift_code_correspondent" />
            <DevForm.Field prefix="main" name="routing_number" />
            <Buttons />
        </Form>
    );
}

function TabAdministration() {
    const commonInfo = useRouteLoaderData("commonInfo");

    if (!commonInfo.account.is_super) {
        return;
    }

    return <RenderForm buttons={<AdminButtons />} />;
}

function DeveloperFinanceModerationBadge({ state }) {
    const { t } = useTranslation();
    switch (state) {
        case DeveloperFinanceModeration.APPROVED:
            return <Badge variant="success">{t("Одобрен")}</Badge>;
        default:
            return null;
    }
}

function SaveButton() {
    const { t } = useTranslation();
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;
    return (
        <>
            {developer.finance_moderation === DeveloperFinanceModeration.APPROVED && (
                <Alert variant="warning" className="mb-3">
                    {t(
                        "ВНИМАНИЕ! После проверки платёжных данных изменения в раздел вносятся по запросу на financeteam@vk.team"
                    )}
                </Alert>
            )}
            <DevForm.Button variant="primary" type="submit" name="action_form" value="main">
                {t("Сохранить")}
            </DevForm.Button>
        </>
    );
}

function Buttons({ legal_info = false }) {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    const commonInfo = useRouteLoaderData("commonInfo");
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;

    const saveButton =
        !commonInfo?.account.is_super && developer.finance_moderation === DeveloperFinanceModeration.APPROVED ? (
            <Alert variant="warning" className="mb-3">
                {t(
                    "ВНИМАНИЕ! После проверки платёжных данных изменения в раздел вносятся по запросу на financeteam@vk.team"
                )}
            </Alert>
        ) : (
            <SaveButton />
        );

    return legal_info ? (
        <>
            {saveButton}{" "}
            {developer.finance_moderation === DeveloperFinanceModeration.NEW && (
                <DevForm.Button
                    variant="warning"
                    type="submit"
                    name="action_form"
                    value="to_moderation"
                    disabled={!apiData.filled_required}
                >
                    {t("Отправить на проверку")}
                </DevForm.Button>
            )}
            {developer.finance_moderation === DeveloperFinanceModeration.REJECTED && (
                <DevForm.Button variant="warning" type="submit" name="action_form" value="to_moderation">
                    {t("Вернуть на проверку")}
                </DevForm.Button>
            )}{" "}
            <div className="float-right">
                {developer.finance_moderation === DeveloperFinanceModeration.ON_MODERATION && (
                    <DevForm.Button variant="danger" type="submit" name="action_form" value="from_moderation">
                        {t("Снять с проверки")}
                    </DevForm.Button>
                )}
            </div>
        </>
    ) : (
        saveButton
    );
}

function AdminButtons() {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const [rejectFinancialModerationShow, setRejectFinancialModerationShow] = useState(false);
    const revalidator = useRevalidator();
    const { api_post_formdata } = useRequest();
    const doAction = usePaymentAction();

    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;

    const rejectFinancialModerationClose = () => {
        setRejectFinancialModerationShow(false);
    };

    return (
        <>
            <SaveButton />{" "}
            {[DeveloperFinanceModeration.ON_MODERATION, DeveloperFinanceModeration.APPROVED].includes(
                developer.finance_moderation
            ) && (
                <>
                    <DevForm.Button variant="info" name="reject" onClick={() => setRejectFinancialModerationShow(true)}>
                        {t("Отклонить")}
                    </DevForm.Button>{" "}
                </>
            )}
            {developer.finance_moderation === DeveloperFinanceModeration.ON_MODERATION && (
                <>
                    <DevForm.Button
                        variant="success"
                        name="approve_financial_moderation"
                        onClick={(ev) => doAction(developer.id, { action: ev.target.name })}
                    >
                        {t("Одобрить")}
                    </DevForm.Button>{" "}
                </>
            )}
            {developer.state === DeveloperState.APPROVED && !developer.my_tracker_id && (
                <>
                    <DevForm.Button
                        variant="dark"
                        name="mytracker_account"
                        onClick={(ev) => doAction(developer.id, { action: ev.target.name })}
                    >
                        {t("Создать аккаунт MyTracker")}
                    </DevForm.Button>{" "}
                </>
            )}
            {developer.state === DeveloperState.APPROVED && !developer.chat_id && !developer.chat_max_url && (
                <>
                    <DevForm.Button
                        variant="dark"
                        name="create_chat"
                        onClick={(ev) => doAction(developer.id, { action: ev.target.name })}
                    >
                        {t("Создать чат")}
                    </DevForm.Button>{" "}
                </>
            )}
            <DevModal
                size="lg"
                show={rejectFinancialModerationShow}
                onHide={rejectFinancialModerationClose}
                header={t("Финансовая модерация")}
                body={
                    <>
                        <DevForm.Field
                            prefix="reject_financial_moderation"
                            name="fast_choice"
                            onChange={(data) => {
                                fieldChange({
                                    prefix: "reject_financial_moderation",
                                    apiData,
                                    setApiData,
                                    name: "fast_choice",
                                    val: (data && data.val) || "",
                                });
                                fieldChange({
                                    prefix: "reject_financial_moderation",
                                    apiData,
                                    setApiData,
                                    name: "moder_comment",
                                    val: (data && data.val) || "",
                                });
                            }}
                        />
                        <DevForm.Field prefix="reject_financial_moderation" name="moder_comment" />
                        <DevForm.Field prefix="reject_financial_moderation" name="service_comment" />
                    </>
                }
                footer={
                    <DevForm.Button
                        variant="primary"
                        name="action"
                        value="reject_financial_moderation"
                        onClick={(ev) => {
                            const rfmApiUrl = url_reverse("api:developer:payment_info_actions", {
                                developer_id: developer.id,
                            });
                            fieldChange({
                                prefix: "reject_financial_moderation",
                                apiData,
                                setApiData,
                                name: "fast_choice",
                                val: "",
                            });
                            const formData = buildFormData({
                                ev,
                                apiData,
                                submitPrefixes: ["reject_financial_moderation"],
                            });
                            api_post_formdata(ev, rfmApiUrl, formData, {
                                then: (resp) => {
                                    if (resp.status === "ok") {
                                        revalidator.revalidate();
                                        alert(t("Письмо отправлено"));
                                        rejectFinancialModerationClose();
                                    } else {
                                        const newApiData = { ...apiData };
                                        newApiData.forms_data.reject_financial_moderation =
                                            resp.forms_data.reject_financial_moderation;
                                        setApiData(newApiData);
                                    }
                                },
                            });
                        }}
                    >
                        {t("Применить")}
                    </DevForm.Button>
                }
                renderWrapper={(children) => <Form>{children}</Form>}
            />
        </>
    );
}

function usePaymentAction() {
    const { setApiReget } = useAppContext();
    const { api_post } = useRequest();
    const revalidator = useRevalidator();

    const doAction = (developerId, params) => {
        api_post(null, url_reverse("api:developer:payment_info_actions", { developer_id: developerId }), {
            params,
            then: (resp) => {
                if (resp.status === "ok") {
                    if (resp.message) {
                        alert(resp.message);
                    }
                    revalidator.revalidate();
                    setApiReget(true);
                }
            },
        });
    };

    return doAction;
}

function isRuKontur(developer) {
    return developer.residence_terr === DeveloperResidenceTerr.VKP_RU && developer.country === DeveloperCountry.RUS;
}

function isEnKontur(developer) {
    return developer.residence_terr === DeveloperResidenceTerr.VKP_OTHER;
}

function DocumentationLink({ url, info = "" }) {
    return (
        <div className="form-group row">
            <div className="col-sm-9 ml-auto">
                <a href={url}>{url}</a> {info}
            </div>
        </div>
    );
}

function handleKeyDownFunction(parts, inputRefs) {
    return (index, e) => {
        const input = e.target;
        const pos = input.selectionStart;
        const len = input.value.length;
        if (e.key === "Backspace" && parts[index] === "" && index > 0) {
            inputRefs[index - 1].current?.focus();
            return;
        }
        if (e.key === "ArrowRight" && pos === len && index < 1) {
            e.preventDefault();
            inputRefs[index + 1].current?.focus();
            setTimeout(() => inputRefs[index + 1].current?.setSelectionRange(0, 0), 0);
            return;
        }
        if (e.key === "ArrowLeft" && pos === 0 && index > 0) {
            e.preventDefault();
            inputRefs[index - 1].current?.focus();
            setTimeout(() => {
                const prev = inputRefs[index - 1].current;
                const l = prev.value.length;
                prev.setSelectionRange(l, l);
            }, 0);
        }
    };
}

function FieldIdDocumentSeriesNumber() {
    const { t } = useTranslation();
    const { apiData } = useAppContext();

    return (
        <FormFieldGroup
            label={t("Серия и номер паспорта")}
            required={apiData.forms_data?.main.fields?.id_document_series.required}
        >
            <Row className="no-gutters">
                <Col md={2}>
                    <DevForm.Field prefix="main" name="id_document_series" bareControl={true} maxLength={4} />
                </Col>
                <Col xs="auto" className="d-flex align-items-center px-1">
                    {" "}
                </Col>
                <Col md={2}>
                    <DevForm.Field prefix="main" name="id_document_number" bareControl={true} maxLength={6} />
                </Col>
            </Row>
            <FormFieldErrorMessage
                error={
                    apiData.forms_data?.main.errors?.id_document_series ||
                    apiData.forms_data?.main.errors?.id_document_number
                }
                custom={true}
            />
        </FormFieldGroup>
    );
}

function FieldIdDocumentDivCode() {
    const { apiData, setApiData } = useAppContext();
    const [parts, setParts] = useState(["", ""]);
    const inputRefs = [useRef(), useRef()];

    const value = apiData.forms_data.main.data.id_document_div_code;
    const error = apiData.forms_data.main.errors.id_document_div_code;

    const onChange = (val) => fieldChange({ prefix: "main", apiData, setApiData, name: "id_document_div_code", val });

    // Форматируем только при первом рендере
    useEffect(() => {
        if (!value) return;
        const asString = String(value);
        const formatRe = /^\d{3}-\d{3}$/;
        if (formatRe.test(asString)) {
            const [a, b] = asString.split("-");
            setParts([a, b]);
        } else {
            const clean = asString.replace(/\D/g, "");
            if (clean.length > 0) {
                const padded = clean.padStart(6, "0").slice(-6);
                setParts([padded.slice(0, 3), padded.slice(3)]);
                onChange(`${padded.slice(0, 3)}-${padded.slice(3)}`);
            }
        }
    }, []);

    const updateValue = (newParts) => {
        setParts(newParts);
        const [a, b] = newParts;
        if (a.length === 3 && b.length === 3) {
            onChange(`${a}-${b}`);
        } else {
            onChange(a + b);
        }
    };

    const handleInputChange = (index, inputValue) => {
        const clean = inputValue.replace(/\D/g, "").slice(0, 3);
        const newParts = [...parts];
        const prevLength = newParts[index].length;
        newParts[index] = clean;
        updateValue(newParts);
        if (clean.length === 3 && clean.length > prevLength && index < 1) {
            setTimeout(() => inputRefs[index + 1].current?.focus(), 0);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
        if (pasted.length >= 1) {
            const p1 = pasted.slice(0, 3);
            const p2 = pasted.slice(3, 6);
            const newParts = [p1, p2];
            updateValue(newParts);
            setTimeout(() => {
                const focusIndex = p2.length === 3 ? 1 : 0;
                inputRefs[focusIndex].current?.focus();
            }, 0);
        }
    };

    return (
        <FormFieldGroup
            label={apiData.forms_data.main.fields.id_document_div_code.label}
            required={apiData.forms_data.main.fields.id_document_div_code.required}
        >
            <Row className="no-gutters">
                <Col md={2}>
                    <Form.Control
                        ref={inputRefs[0]}
                        value={parts[0]}
                        onChange={(e) => handleInputChange(0, e.target.value)}
                        onKeyDown={(e) => handleKeyDownFunction(0, e)}
                        onPaste={handlePaste}
                        maxLength={3}
                        className="text-center mr-2"
                        placeholder="XXX"
                    />
                </Col>
                <Col xs="auto" className="d-flex align-items-center px-1">
                    -
                </Col>
                <Col md={2}>
                    <Form.Control
                        ref={inputRefs[1]}
                        value={parts[1]}
                        onChange={(e) => handleInputChange(1, e.target.value)}
                        onKeyDown={(e) => handleKeyDownFunction(1, e)}
                        onPaste={handlePaste}
                        maxLength={3}
                        className="text-center"
                        placeholder="XXX"
                    />
                </Col>
            </Row>
            {error && <FormFieldErrorMessage error={error} custom />}
        </FormFieldGroup>
    );
}

function FieldSnils() {
    const { apiData, setApiData } = useAppContext();
    const [parts, setParts] = useState(["", "", "", ""]);
    const inputRefs = [useRef(), useRef(), useRef(), useRef()];

    const value = apiData.forms_data.main.data.snils || "";
    const error = apiData.forms_data.main.errors.snils;

    const onChange = (val) => fieldChange({ prefix: "main", apiData, setApiData, name: "snils", val });

    // Парсим значение при изменении пропса value
    useEffect(() => {
        if (value) {
            // Убираем все не-цифры
            const cleanValue = value.replace(/\D/g, "");
            if (cleanValue.length === 11) {
                setParts([
                    cleanValue.slice(0, 3),
                    cleanValue.slice(3, 6),
                    cleanValue.slice(6, 9),
                    cleanValue.slice(9, 11),
                ]);
            }
        }
    }, [value]);

    const handleInputChange = (index, inputValue) => {
        const newParts = [...parts];
        const maxLengths = [3, 3, 3, 2];
        const cleanValue = inputValue.replace(/\D/g, "");
        const previousLength = newParts[index].length;
        const trimmedValue = cleanValue.slice(0, maxLengths[index]);

        newParts[index] = trimmedValue;
        setParts(newParts);

        // Форматируем в вид 000-000-000 00
        const formattedValue =
            `${newParts[0]}` +
            (newParts[0].length === 3 ? `-${newParts[1]}` : newParts[1] ? `-${newParts[1]}` : "") +
            (newParts[1].length === 3 ? `-${newParts[2]}` : newParts[2] ? `-${newParts[2]}` : "") +
            (newParts[2].length === 3 ? ` ${newParts[3]}` : newParts[3] ? ` ${newParts[3]}` : "");

        onChange(formattedValue);

        // Автопереход если поле заполнено и мы добавили символ
        if (trimmedValue.length === maxLengths[index] && trimmedValue.length > previousLength && index < 3) {
            setTimeout(() => {
                inputRefs[index + 1].current?.focus();
            }, 0);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text").replace(/\D/g, "");

        if (pastedText.length >= 1) {
            const maxLengths = [3, 3, 3, 2];
            const newParts = ["", "", "", ""];

            // Распределяем цифры по полям
            let remainingText = pastedText;
            for (let i = 0; i < 4 && remainingText.length > 0; i++) {
                const partLength = Math.min(maxLengths[i], remainingText.length);
                newParts[i] = remainingText.slice(0, partLength);
                remainingText = remainingText.slice(partLength);
            }

            setParts(newParts);

            // Форматируем результат
            const formattedValue =
                `${newParts[0]}` +
                (newParts[0].length === 3 ? `-${newParts[1]}` : newParts[1] ? `-${newParts[1]}` : "") +
                (newParts[1].length === 3 ? `-${newParts[2]}` : newParts[2] ? `-${newParts[2]}` : "") +
                (newParts[2].length === 3 ? ` ${newParts[3]}` : newParts[3] ? ` ${newParts[3]}` : "");

            onChange(formattedValue);

            // Фокус на последнее заполненное поле
            setTimeout(() => {
                let lastFilledIndex = 0;
                for (let i = 3; i >= 0; i--) {
                    if (newParts[i].length > 0) {
                        lastFilledIndex = i;
                        break;
                    }
                }
                inputRefs[lastFilledIndex].current?.focus();
            }, 0);
        }
    };

    return (
        <FormFieldGroup
            label={apiData.forms_data.main.fields.snils.label}
            required={apiData.forms_data.main.fields.snils.required}
        >
            <Row className="no-gutters">
                <Col xs="auto">
                    <Form.Control
                        ref={inputRefs[0]}
                        type="text"
                        value={parts[0]}
                        onChange={(e) => handleInputChange(0, e.target.value)}
                        onKeyDown={(e) => handleKeyDownFunction(0, e)}
                        onPaste={handlePaste}
                        maxLength={3}
                        style={{ width: "60px", textAlign: "center" }}
                        placeholder="XXX"
                    />
                </Col>

                <Col xs="auto" className="d-flex align-items-center px-1">
                    <span>-</span>
                </Col>

                <Col xs="auto">
                    <Form.Control
                        ref={inputRefs[1]}
                        type="text"
                        value={parts[1]}
                        onChange={(e) => handleInputChange(1, e.target.value)}
                        onKeyDown={(e) => handleKeyDownFunction(1, e)}
                        maxLength={3}
                        style={{ width: "60px", textAlign: "center" }}
                        placeholder="XXX"
                    />
                </Col>

                <Col xs="auto" className="d-flex align-items-center px-1">
                    <span>-</span>
                </Col>

                <Col xs="auto">
                    <Form.Control
                        ref={inputRefs[2]}
                        type="text"
                        value={parts[2]}
                        onChange={(e) => handleInputChange(2, e.target.value)}
                        onKeyDown={(e) => handleKeyDownFunction(2, e)}
                        maxLength={3}
                        style={{ width: "60px", textAlign: "center" }}
                        placeholder="XXX"
                    />
                </Col>

                <Col xs="auto" className="d-flex align-items-center px-1">
                    {" "}
                </Col>

                <Col xs="auto">
                    <Form.Control
                        ref={inputRefs[3]}
                        type="text"
                        value={parts[3]}
                        onChange={(e) => handleInputChange(3, e.target.value)}
                        onKeyDown={(e) => handleKeyDownFunction(3, e)}
                        maxLength={2}
                        style={{ width: "50px", textAlign: "center" }}
                        placeholder="XX"
                    />
                </Col>
            </Row>

            {error && <FormFieldErrorMessage error={error} custom={true} />}
        </FormFieldGroup>
    );
}

function FieldTaxId() {
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;
    const prefix = "main";
    const name = "tax_id";

    if (
        isRuKontur(developer) &&
        [DeveloperLegalForm.LEGAL, DeveloperLegalForm.INDIVIDUAL].includes(developer.legal_form)
    ) {
        const { apiData, setApiData } = useAppContext();
        const suggestionsRef = useRef(null);
        const form = apiData.forms_data[prefix];
        const field = form.fields[name];
        const value = form.data[name] || "";

        if (field.widget.is_hidden) {
            return;
        }

        const handleChange = (event) => {
            fieldChange({ prefix, apiData, setApiData, name, val: event.target.value });
        };

        const handleSuggestionChange = (e) => {
            // Костыль, чтобы сразу выбранное значение подставлялось в input
            // Без него периодически вместо ИНН подставляется текстовое имя организации
            suggestionsRef.current.setInputValue(e.data.inn);

            const { data } = e;
            const addressData = data.address?.data || {};

            const updatedFields = {
                company_ru: e.value,
                tax_id: data.inn,
                kpp: data.kpp,
                registration_number: data.ogrn,
                region: addressData.region_with_type,
                region_rf: addressData.region_kladr_id
                    ? parseInt(addressData.region_kladr_id.substring(0, 2), 10)
                    : undefined,
                city: addressData.city_with_type || "",
                street: addressData.street_with_type || "",
                house: addressData.house || "",
                building: addressData.block_type
                    ? `${addressData.block_type} ${addressData.block}`
                    : addressData.block || "",
                postal_code: addressData.postal_code,
                surname: data.fio?.surname || "",
                forename: data.fio?.name || "",
                patronymic: data.fio?.patronymic || "",
                payment_receiver: data.name.short_with_opf,
            };

            Object.keys(updatedFields).forEach((f) => {
                if (updatedFields[f] !== undefined) {
                    fieldChange({ prefix, apiData, setApiData, name: f, val: updatedFields[f] });
                }
            });
        };

        return (
            <FormFieldGroup label={field.label} required={field.required}>
                <PartySuggestions
                    url={"/dev/api/dadata/party/"}
                    ref={suggestionsRef}
                    httpCache={true}
                    inputProps={{
                        placeholder: field.widget.attrs.placeholder,
                        // Вызывается при вводе в input
                        onInput: handleChange,
                    }}
                    filterStatus={["ACTIVE"]}
                    token={null}
                    value={{ value }}
                    // Вызывается только при выборе подсказки
                    onChange={handleSuggestionChange}
                />
                <FormFieldErrorMessage error={form.errors[name]} custom={true} />
                <FormFieldLenCount maxLength={field.max_length} disabled={field.disabled} val={value} />
                <FormFieldHelpText help_text={field.help_text} />
            </FormFieldGroup>
        );
    }

    return <DevForm.Field prefix={prefix} name={name} />;
}

function FieldBicCode() {
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const { developer } = developerCommonInfo;
    const prefix = "main";
    const name = "bic_code";

    if (isRuKontur(developer)) {
        const { apiData, setApiData } = useAppContext();
        const suggestionsRef = useRef(null);
        const form = apiData.forms_data[prefix];
        const field = form.fields[name];
        const widget = field.widget || {};
        const value = form.data[name] || "";

        const handleChange = (event) => {
            fieldChange({ prefix, apiData, setApiData, name, val: event.target.value });
        };

        return (
            <FormFieldGroup label={field.label} required={field.required}>
                <BankSuggestions
                    url={"/dev/api/dadata/bank/"}
                    ref={suggestionsRef}
                    httpCache={true}
                    inputProps={{
                        placeholder: widget.attrs.placeholder,
                        onInput: handleChange,
                    }}
                    filterStatus={["ACTIVE"]}
                    token={null}
                    value={{ value }}
                    onChange={(e) => {
                        suggestionsRef.current.setInputValue(e.data.bic);
                        fieldChange({ prefix, apiData, setApiData, name, val: e.data.bic });
                        fieldChange({ prefix, apiData, setApiData, name: "beneficiary_bank_name", val: e.value });
                        fieldChange({
                            prefix,
                            apiData,
                            setApiData,
                            name: "beneficiary_bank_address",
                            val: e.data.address.value,
                        });
                        fieldChange({
                            prefix,
                            apiData,
                            setApiData,
                            name: "corr_acc_bank",
                            val: e.data.correspondent_account,
                        });
                    }}
                />
                <FormFieldErrorMessage error={form.errors[name]} custom={true} />
                <FormFieldLenCount maxLength={field.max_length} disabled={field.disabled} val={value} />
                <FormFieldHelpText help_text={field.help_text} />
            </FormFieldGroup>
        );
    }

    return <DevForm.Field prefix={prefix} name={name} />;
}
